/**
 * BigSplice Delete Confirmation Component
 * Double-confirm delete dialog (button + type "delete")
 */

import React, { useState, KeyboardEvent } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { AlertTriangle, Trash2, X } from 'lucide-react';
import { cn } from '../../lib/utils';

// Inline type
type ConfirmationStep = 'button' | 'text' | 'confirmed';

interface DeleteConfirmationProps {
  fileName: string;
  fileType: 'file' | 'folder';
  step: ConfirmationStep;
  typedText: string;
  onConfirmStep1: () => void;
  onConfirmStep2: (text: string) => Promise<boolean>;
  onCancel: () => void;
  onUpdateTypedText: (text: string) => void;
}

export const DeleteConfirmation: React.FC<DeleteConfirmationProps> = ({
  fileName,
  fileType,
  step,
  typedText,
  onConfirmStep1,
  onConfirmStep2,
  onCancel,
  onUpdateTypedText,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleStep2Submit = async () => {
    if (typedText.toLowerCase() !== 'delete') return;

    setIsDeleting(true);
    try {
      await onConfirmStep2(typedText);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleStep2Submit();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const isDeleteEnabled = typedText.toLowerCase() === 'delete';

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            {step === 'button' ? 'Confirm Deletion' : 'Final Confirmation'}
          </DialogTitle>
          <DialogDescription>
            {step === 'button' ? (
              <>
                You are about to delete the {fileType}{' '}
                <strong className="text-foreground">"{fileName}"</strong>.
                This action cannot be undone.
              </>
            ) : (
              <>
                Type <strong className="text-foreground">"delete"</strong> to
                permanently remove{' '}
                <strong className="text-foreground">"{fileName}"</strong>.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Button Confirmation */}
        {step === 'button' && (
          <div className="py-4">
            <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-600 dark:text-yellow-400">
                    Warning
                  </p>
                  <p className="text-muted-foreground mt-1">
                    {fileType === 'folder'
                      ? 'All files and subfolders within this folder will be permanently deleted.'
                      : 'This file and all its data will be permanently removed from your account.'}
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={onCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button variant="destructive" onClick={onConfirmStep1}>
                <Trash2 className="h-4 w-4 mr-2" />
                Confirm Delete
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 2: Text Confirmation */}
        {step === 'text' && (
          <div className="py-4">
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-destructive">
                    This action is irreversible
                  </p>
                  <p className="text-muted-foreground mt-1">
                    Once deleted, "{fileName}" cannot be recovered.
                    Type "delete" below to proceed.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="delete-confirm" className="text-sm font-medium">
                Type <code className="bg-muted px-1.5 py-0.5 rounded">delete</code> to confirm:
              </label>
              <Input
                id="delete-confirm"
                type="text"
                placeholder="Type 'delete' here"
                value={typedText}
                onChange={(e) => onUpdateTypedText(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                className={cn(
                  'transition-colors',
                  typedText.length > 0 && !isDeleteEnabled && 'border-yellow-500',
                  isDeleteEnabled && 'border-destructive'
                )}
              />
              {typedText.length > 0 && !isDeleteEnabled && (
                <p className="text-xs text-yellow-600">
                  Please type "delete" exactly to confirm
                </p>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0 mt-4">
              <Button variant="outline" onClick={onCancel} disabled={isDeleting}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleStep2Submit}
                disabled={!isDeleteEnabled || isDeleting}
              >
                {isDeleting ? (
                  <>Deleting...</>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Permanently
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DeleteConfirmation;
